import { Camera, Users, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { DirectorCalendarShooting } from '@/services/data/directorCalendar';

interface DirectorShootingCardProps {
  shooting: DirectorCalendarShooting;
}

export function DirectorShootingCard({ shooting }: DirectorShootingCardProps) {
  const navigate = useNavigate();

  const customerName = [shooting.customer.first_name, shooting.customer.last_name]
    .filter(Boolean)
    .join(' ') || 'Client';

  const isPast = shooting.status === 'completed';

  const handleClick = () => {
    // Navigate to director dashboard filtered on this shooting
    navigate(`/app/director-dashboard?shootingId=${shooting.id}`);
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "w-full text-left glass rounded-xl border p-3 transition-all",
        "hover:scale-[1.01] active:scale-[0.99]",
        isPast
          ? "opacity-60 border-emerald-500/30"
          : "border-primary/30 hover:border-primary/50"
      )}
    >
      {/* Header: Customer avatar + name + status */}
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
        <Avatar className="h-6 w-6">
          <AvatarImage src={shooting.customer.avatar_url || ''} />
          <AvatarFallback className="text-[10px] bg-primary/20 text-primary">
            {(shooting.customer.first_name?.[0] || '') + (shooting.customer.last_name?.[0] || '')}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium text-foreground truncate flex-1">
          {customerName}
        </span>
        {isPast ? (
          <Badge variant="outline" className="text-[10px] border-emerald-500/40 text-emerald-400">
            Terminé
          </Badge>
        ) : (
          <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
            À venir
          </Badge>
        )}
      </div>

      {/* Body: Shooting info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-primary" />
          <span className="text-sm text-foreground">
            {shooting.title || 'Tournage'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {shooting.contentCount > 0 && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              {shooting.contentCount} sujets
            </span>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>

      <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
        {shooting.plan && (
          <Badge variant="secondary" className="text-[10px]">
            {shooting.plan}
          </Badge>
        )}
        {shooting.directorName && (
          <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-400">
            🎬 {shooting.directorName}
          </Badge>
        )}
      </div>
    </button>
  );
}
